/**
 * PolicyHistoryDialog — the Policies-page "See history" / "Verlauf anzeigen"
 * control (dms/spec.md §4.3, §M12): reveals a policy's OLDER (historized)
 * versions — the current one is already the grid row itself — each with an
 * "Öffnen" (→ the M11 inline viewer, `/ablage/$id`) and "Herunterladen"
 * action against that version's source PDF (`source_document_id`).
 *
 * Lazy-loaded: `usePolicyVersions` only fetches while `open` is true, so
 * clicking "Verlauf anzeigen" on one row doesn't fetch history for every row.
 *
 * DMS Part2 P2-13 (§1.3): each row also carries a `DeleteDocumentButton` for that
 * version's `source_document_id` — it shows itself only when `document.delete` is
 * held AND that specific document is live-`inactive` (re-checked via `GET
 * /documents/:id`, not inferred from `valid_to`, since a document's state can be
 * toggled independently via Ablage's own `document.set_state`).
 *
 * [Source: apps/web/src/features/entity-detail/PolicyAvbSection.tsx] (small-dialog-over-a-list precedent)
 * [Source: apps/web/src/features/documents/components/DeleteDocumentButton.tsx]
 * [Source: projects/broker-ag/dms/spec.md §4.3, §M12]
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §1.3]
 */
import { Download, ExternalLink } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Dialog } from '../../administration/components';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { chf, dateShortDe } from '../../../lib/format';
import { saveBlob } from '../../../lib/save-blob';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { usePolicyVersions } from '../hooks/usePolicyVersions';
import { DeleteDocumentButton } from '../../documents/components/DeleteDocumentButton';

export function PolicyHistoryDialog({
  policyId,
  policyNr,
  open,
  onClose,
}: {
  policyId: string;
  policyNr: string;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const live = isLiveData();
  const { versions, isLoading, isError } = usePolicyVersions(policyId, open);

  async function handleDownload(documentId: string, label: string) {
    if (!live) return; // mock mode: no real bytes to save — Öffnen still works via content_url.
    try {
      // DL-2 fix (wave dms-fixC): "Herunterladen" is a genuine download —
      // `document.download` (`/download`), NOT `.content`'s `document.read` inline bytes.
      const blob = await apiClient.documents.download(documentId);
      saveBlob(blob, label);
    } catch {
      // Best-effort convenience action — Öffnen (the inline viewer) is the
      // authoritative surface, same posture as DocumentViewerScreen's own download button.
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Verlauf · ${policyNr}`}
      description="Ältere, historisierte Versionen dieser Police."
    >
      {isLoading && <p className="text-sm text-muted-foreground">Lade Verlauf …</p>}
      {isError && (
        <p role="alert" className="text-sm text-destructive">
          Der Verlauf konnte nicht geladen werden.
        </p>
      )}
      {!isLoading && !isError && versions.length === 0 && (
        <p className="text-sm text-muted-foreground">Keine älteren Versionen vorhanden.</p>
      )}
      {versions.length > 0 && (
        <ul className="flex flex-col gap-2" data-testid="policy-history-list">
          {versions.map((v) => (
            <li
              key={v.id}
              className="flex items-center gap-3 rounded-lg border bg-background px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  Version {v.version_nr}
                  {v.valid_to === null && <Badge variant="success">Aktuell</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  gültig {dateShortDe(v.valid_from)}
                  {v.valid_to ? ` - ${dateShortDe(v.valid_to)}` : ' - heute'}
                  {v.premium_gross ? ` · ${chf(Number(v.premium_gross))}` : ''}
                </div>
              </div>
              {v.source_document_id && (
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const documentId = v.source_document_id!;
                      onClose();
                      void navigate({ to: '/ablage/$id', params: { id: documentId } });
                    }}
                  >
                    <ExternalLink className="size-3.5" /> Öffnen
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    aria-label={`Version ${v.version_nr} herunterladen`}
                    onClick={() =>
                      void handleDownload(v.source_document_id!, `${policyNr}-v${v.version_nr}.pdf`)
                    }
                  >
                    <Download className="size-3.5" />
                  </Button>
                  <DeleteDocumentButton
                    documentId={v.source_document_id}
                    label={`${policyNr} · Version ${v.version_nr}`}
                    className="size-8"
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
